import React, { FC } from 'react';

import { StripeElementLocale, StripeElementsOptions } from '@stripe/stripe-js';
import { SxProps, Theme } from '@mui/system';
import { Elements } from '@stripe/react-stripe-js';

import {
  language,
  PaymentPlans,
  pharmacyBillingSelector,
  stripeCurrency,
} from '@pharmaplan/common';
import { useAppSelector } from '../../hooks/useAppSelector';
import { stripePromise } from '../../Stripe/StripeWrapper';
import { store } from '../../store';
import { setDialog } from '../../reducers/dialogReducer';
import OutcomeModal from '../Modals/OutcomeModal';
import { OutcomeModalTypes } from '../../helpers/Constants';
import strings from '../../localization';

import PaymentComponent from './PaymentComponent';

interface IPaymentGateway {
  paymentPlan: PaymentPlans;
  showFees?: boolean;
  showTerms?: boolean;
  customTitle?: string;
  customSubmitLabel?: string;
  customContainer?: SxProps<Theme>;
  isDialog?: boolean;
}

export const showPaymentError = (
  dispatch: typeof store.dispatch,
  message: string,
) => {
  dispatch(
    setDialog({
      heading: {
        title: '',
      },
      Component: (
        <OutcomeModal
          type={OutcomeModalTypes.error}
          message={message}
          customCloseLabel={strings.retryAndUpdateCard}
        />
      ),
      showCloseButton: true,
    }),
  );
};

const PaymentGateway: FC<IPaymentGateway> = ({
  paymentPlan,
  showFees = true,
  showTerms = true,
  customTitle,
  customSubmitLabel,
  customContainer,
  isDialog,
}) => {
  const billing = useAppSelector(pharmacyBillingSelector);
  const { unlimitedFees } = billing ?? {};

  const userLanguage = useAppSelector(language);

  const isUnlimitedPlan = paymentPlan === PaymentPlans.unlimited;
  const paymentMode = isUnlimitedPlan
    ? ('payment' as const)
    : ('setup' as const);

  const options: StripeElementsOptions = {
    mode: paymentMode,
    currency: stripeCurrency,
    locale: (userLanguage ?? 'en-CA') as StripeElementLocale,
    payment_method_types: ['card'],
  };

  if (isUnlimitedPlan) {
    options.amount = unlimitedFees ?? 0;
    options.setupFutureUsage = 'off_session';
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentComponent
        customSubmitLabel={customSubmitLabel}
        customContainer={customContainer}
        customTitle={customTitle}
        paymentPlan={paymentPlan}
        showTerms={showTerms}
        showFees={showFees}
        isDialog={isDialog}
      />
    </Elements>
  );
};

export default PaymentGateway;
